import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";
import { TYPE_BADGE_COLORS } from "../../utils/typeColors";

interface Props { payload: VisualPayload }

interface MoveStats {
  move_name: string;
  move_type: string;
  poke_type: string;
  power: number | null;
  energy_delta: number | null;
  turns: number | null;
  dpt: number | null;
  ept: number | null;
  dpe: number | null;
  dps: number | null;
  is_elite: boolean;
  learners: string[];
  learner_count: number;
  // plan phase 5 (decision 0022): the PvP tier and what it rests on
  tier?: string;
  tier_label?: string;
  tier_reason?: string;
  pvp_power?: number | null;
  pvp_energy_delta?: number | null;
  pvp_turns?: number | null;
  pvp_effect?: string;
  adoption?: { league?: string; learners?: number; recommended?: number; top?: { name: string; rank: number }[] } | null;
}

const TIER_STYLE: Record<string, string> = {
  great:       "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  good:        "bg-sky-500/20 text-sky-300 border-sky-500/30",
  situational: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  poor:        "bg-white/10 text-white/60 border-white/15",
  terrible:    "bg-rose-500/20 text-rose-300 border-rose-500/30",
};

/** The tier badge, its reason and the Great League adoption line (plan phase 5). */
export function TierBlock({ sec }: { sec: Pick<MoveStats, "tier" | "tier_label" | "tier_reason" | "pvp_effect" | "adoption" | "pvp_power" | "pvp_energy_delta" | "pvp_turns" | "move_type"> }) {
  if (!sec.tier) return null;
  const a = sec.adoption ?? undefined;
  const top = (a?.top ?? []).map((t) => `${t.name} #${t.rank}`).join(" · ");
  const pvp = sec.pvp_power != null && sec.pvp_energy_delta != null
    ? (sec.move_type === "fast" ? `${sec.pvp_power} dmg · ${sec.pvp_energy_delta} energy · ${sec.pvp_turns ?? "?"} turns` : `${sec.pvp_power} dmg · ${Math.abs(sec.pvp_energy_delta)} energy`)
    : null;
  return (
    <div className="mb-4 rounded-lg border border-white/[0.07] p-3 space-y-1.5">
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border leading-none ${TIER_STYLE[sec.tier] ?? TIER_STYLE.poor}`}>{sec.tier_label ?? sec.tier}</span>
        <span className="text-[10px] text-white/30 uppercase tracking-wide">PvP tier</span>
      </div>
      {sec.tier_reason && <p className="text-xs text-white/70">{sec.tier_reason}{sec.pvp_effect ? `; ${sec.pvp_effect}` : ""}</p>}
      {pvp && <p className="text-[11px] text-white/45">PvP: {pvp}</p>}
      {a && typeof a.learners === "number" && (
        <p className="text-[11px] text-white/45">
          Chosen on {a.recommended ?? 0} of {a.learners} ranked Great League learners{top ? ` · ${top}` : ""}
        </p>
      )}
    </div>
  );
}

export default function MoveDetailCard({ payload }: Props) {
  const sec = payload.sections.find((s) => s.section === "move_stats") as (MoveStats & { section: string }) | undefined;

  if (!sec) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
        <p className="text-sm text-white/40">No move data.</p>
      </CardShell>
    );
  }

  const typeBadge = TYPE_BADGE_COLORS[sec.poke_type?.toLowerCase()] ?? "bg-white/10 text-white/50";
  const isFast = sec.move_type === "fast";

  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-lg font-semibold text-white">{sec.move_name}</h2>
        {sec.poke_type && (
          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${typeBadge}`}>{sec.poke_type}</span>
        )}
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 text-white/60 font-medium">
          {isFast ? "Fast" : "Charged"}
        </span>
        {sec.is_elite && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-400 font-medium">Elite</span>
        )}
      </div>

      <TierBlock sec={sec} />

      {/* Stat grid */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-white/[0.04] rounded-lg p-3 text-center">
          <p className="text-[11px] text-white/30 uppercase tracking-wide mb-1">Power</p>
          <p className="text-lg font-semibold text-white">{sec.power ?? "—"}</p>
        </div>
        <div className="bg-white/[0.04] rounded-lg p-3 text-center">
          <p className="text-[11px] text-white/30 uppercase tracking-wide mb-1">Energy</p>
          <p className="text-lg font-semibold text-white">
            {sec.energy_delta != null
              ? (sec.energy_delta > 0 ? `+${sec.energy_delta}` : sec.energy_delta)
              : "—"}
          </p>
        </div>
        {isFast && sec.turns != null && (
          <div className="bg-white/[0.04] rounded-lg p-3 text-center">
            <p className="text-[11px] text-white/30 uppercase tracking-wide mb-1">Turns</p>
            <p className="text-lg font-semibold text-white">{sec.turns}</p>
          </div>
        )}
      </div>

      {/* Derived stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {sec.dpt != null && (
          <div className="bg-white/[0.04] rounded-lg p-3 text-center">
            <p className="text-[11px] text-white/30 uppercase tracking-wide mb-1">DPT</p>
            <p className="text-base font-semibold text-white">{sec.dpt.toFixed(2)}</p>
          </div>
        )}
        {isFast && sec.ept != null && (
          <div className="bg-white/[0.04] rounded-lg p-3 text-center">
            <p className="text-[11px] text-white/30 uppercase tracking-wide mb-1">EPT</p>
            <p className="text-base font-semibold text-white">{sec.ept.toFixed(2)}</p>
          </div>
        )}
        {!isFast && sec.dpe != null && (
          <div className="bg-white/[0.04] rounded-lg p-3 text-center">
            <p className="text-[11px] text-white/30 uppercase tracking-wide mb-1">DPE</p>
            <p className="text-base font-semibold text-white">{sec.dpe.toFixed(2)}</p>
          </div>
        )}
        {sec.dps != null && (
          <div className="bg-white/[0.04] rounded-lg p-3 text-center">
            <p className="text-[11px] text-white/30 uppercase tracking-wide mb-1">DPS (PvE)</p>
            <p className="text-base font-semibold text-white">{sec.dps.toFixed(2)}</p>
          </div>
        )}
      </div>

      {/* Learners */}
      {sec.learner_count > 0 && (
        <div>
          <p className="text-[11px] text-white/30 uppercase tracking-wide mb-2">
            Learned by {sec.learner_count} Pokémon
          </p>
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
            {sec.learners.map((name) => (
              <span key={name} className="text-[11px] px-2 py-0.5 rounded-full bg-white/[0.06] text-white/60">
                {name}
              </span>
            ))}
          </div>
        </div>
      )}
    </CardShell>
  );
}
